// Administration / Finance Workspace: Pro & Enterprise only.
//
// A single page with internal tabs (Overview / Income / Expenses / Deposits /
// Vehicle Costs / Documents / Reports). When the API returns 403 with
// `error_code: "plan_upgrade_required"` we render an upgrade card instead of
// the workspace. We deliberately keep the UX simple and daily-business focused;
// this is NOT accounting software and the disclaimer says so explicitly.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, Plus, Trash2, Edit3, X, FileDown, AlertTriangle,
  PiggyBank, TrendingUp, TrendingDown, ListChecks, FileText, ChartBar,
  Receipt, Loader2,
} from "lucide-react";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";
import UpgradeCard from "../components/UpgradeCard";
import {
  RECORD_TYPES, recordMeta, PAYMENT_METHODS, STATUS_OPTIONS, DOC_TYPES,
  fmtPHP, todayISO, statusTone,
} from "../lib/finance";


const TABS = [
  { key: "overview",   label: "Overview",     icon: ChartBar },
  { key: "income",     label: "Income",       icon: TrendingUp },
  { key: "expenses",   label: "Expenses",     icon: TrendingDown },
  { key: "deposits",   label: "Deposits",     icon: PiggyBank },
  { key: "vehicles",   label: "Vehicle costs", icon: Receipt },
  { key: "documents",  label: "Documents",    icon: FileText },
  { key: "reports",    label: "Reports",      icon: ListChecks },
];


export default function Administration() {
  const { user } = useAuth();
  const role = user?.account_type || user?.role;
  const isOwner = role === "business_owner" || role === "platform_admin";

  const [tab, setTab] = useState("overview");
  const [planError, setPlanError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [records, setRecords] = useState([]);
  const [docs, setDocs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRecord, setEditingRecord] = useState(null);  // {mode, data}
  const [editingDoc, setEditingDoc] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setPlanError(null);
    try {
      const [ov, recs, doclist, vs, bs, rp] = await Promise.all([
        api.get("/administration/overview"),
        api.get("/administration/records"),
        api.get("/administration/documents"),
        api.get("/vehicles"),
        api.get("/bookings"),
        api.get("/administration/reports"),
      ]);
      setOverview(ov.data);
      setRecords(recs.data.items || []);
      setDocs(doclist.data.items || []);
      setVehicles(vs.data || []);
      setBookings(bs.data || []);
      setReports(rp.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "plan_upgrade_required") {
        setPlanError(detail);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Plan-locked screen (Basic plan)
  if (!loading && planError) {
    return (
      <UpgradeCard
        detail={planError} isOwner={isOwner}
        title="Unlock Administration tools"
        features={[
          "Rental income, expenses, deposits, and refunds in one ledger",
          "Vehicle-level cost tracking (repairs, fuel, insurance, cleaning)",
          "Monthly overview cards + CSV export",
          "Document storage (permits, OR/CR, insurance, agreements)",
        ]}
      />
    );
  }

  const incomeRecords   = records.filter((r) => recordMeta(r.record_type).group === "income");
  const expenseRecords  = records.filter((r) => recordMeta(r.record_type).group === "expense");
  const depositRecords  = records.filter((r) => recordMeta(r.record_type).group === "deposit");
  const vehicleCostRecords = records.filter((r) =>
    ["vehicle_cost", "maintenance_cost", "repair_cost",
     "fuel_cost", "insurance_cost", "registration_cost",
     "cleaning_cost"].includes(r.record_type)
  );

  const startNew = (preset) => {
    setEditingRecord({
      mode: "new",
      data: {
        record_type: preset?.record_type || "income",
        title: "",
        amount: "",
        currency: "PHP",
        payment_method: "cash",
        status: "paid",
        transaction_date: todayISO(),
        linked_booking_id: preset?.linked_booking_id || null,
        linked_vehicle_id: preset?.linked_vehicle_id || null,
        customer_name: "",
        vendor_name: "",
        description: "",
        category: "",
        receipt_url: "",
      },
    });
  };

  const exportCsv = () => {
    const token = localStorage.getItem("confirmph_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/administration/export.csv`;
    // open in a new window with Authorization via fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `confirmph-finance-${todayISO()}.csv`;
        a.click();
      })
      .catch((err) => toast.error("Export failed: " + err.message));
  };

  return (
    <div className="space-y-6" data-testid="administration-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
            <Wallet className="w-3.5 h-3.5" /> Business management
          </div>
          <h1 className="font-display text-3xl font-bold mt-1">Administration</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Track rental income, expenses, deposits, vehicle costs, and business documents in one
            place. <strong>For internal tracking only</strong>, not a substitute for accounting or tax advice.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
          >
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
          <button
            onClick={() => startNew()}
            data-testid="admin-add-record-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg btn-brand text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Add record
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 w-fit" data-testid="admin-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition inline-flex items-center gap-1.5 ${
              tab === key ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Disclaimer banner */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 inline-flex items-start gap-2" data-testid="admin-disclaimer">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Finance tools are for internal tracking only and do not replace professional accounting or
          tax advice. Always verify totals against your official records.
        </span>
      </div>

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-500" data-testid="admin-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          <p className="mt-2 text-sm">Loading…</p>
        </div>
      ) : (
        <>
          {tab === "overview"  && <OverviewPanel ov={overview} onAdd={startNew} />}
          {tab === "income"    && <RecordList rows={incomeRecords}     title="Income"    onEdit={setEditingRecord} onDelete={loadAll} startNew={() => startNew({record_type: "income"})}    showCustomer />}
          {tab === "expenses"  && <RecordList rows={expenseRecords}    title="Expenses"  onEdit={setEditingRecord} onDelete={loadAll} startNew={() => startNew({record_type: "expense"})}   showVendor />}
          {tab === "deposits"  && <RecordList rows={depositRecords}    title="Deposits"  onEdit={setEditingRecord} onDelete={loadAll} startNew={() => startNew({record_type: "deposit_received"})} showCustomer />}
          {tab === "vehicles"  && <RecordList rows={vehicleCostRecords} title="Vehicle costs" onEdit={setEditingRecord} onDelete={loadAll} startNew={() => startNew({record_type: "fuel_cost"})} showVendor />}
          {tab === "documents" && <DocumentsPanel docs={docs} onEdit={setEditingDoc} onReload={loadAll} onAdd={() => setEditingDoc({mode: "new", data: {document_type: "vehicle_registration", title: "", notes: "", file_url: "", expiry_date: "", linked_vehicle_id: null, linked_booking_id: null}})} />}
          {tab === "reports"   && <ReportsPanel rp={reports} />}
        </>
      )}

      {editingRecord && (
        <RecordEditor
          editing={editingRecord}
          setEditing={setEditingRecord}
          vehicles={vehicles}
          bookings={bookings}
          onClose={() => setEditingRecord(null)}
          onSaved={loadAll}
        />
      )}
      {editingDoc && (
        <DocumentEditor
          editing={editingDoc}
          setEditing={setEditingDoc}
          vehicles={vehicles}
          onClose={() => setEditingDoc(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Overview tab: month summary

function OverviewPanel({ ov, onAdd }) {
  if (!ov) return null;
  const cards = [
    { label: "Income this month",   value: fmtPHP(ov.income_this_month),          tone: "emerald", icon: TrendingUp },
    { label: "Expenses this month", value: fmtPHP(ov.expenses_this_month),        tone: "rose",    icon: TrendingDown },
    { label: "Net estimate",        value: fmtPHP(ov.net_estimate_this_month),    tone: ov.net_estimate_this_month >= 0 ? "emerald" : "rose", icon: Wallet },
    { label: "Vehicle costs",       value: fmtPHP(ov.vehicle_costs_this_month),   tone: "slate",   icon: Receipt },
    { label: "Deposits held",       value: fmtPHP(ov.deposits_held),              tone: "amber",   icon: PiggyBank },
    { label: "Unpaid / pending",    value: fmtPHP(ov.unpaid_pending_total),       tone: "amber",   icon: ListChecks },
  ];
  return (
    <div className="space-y-4" data-testid="admin-overview-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c, i) => {
          const Icon = c.icon;
          const toneClass = c.tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                          : c.tone === "rose"    ? "bg-rose-50 border-rose-200 text-rose-900"
                          : c.tone === "amber"   ? "bg-amber-50 border-amber-200 text-amber-900"
                          : "bg-slate-50 border-slate-200 text-slate-900";
          return (
            <div key={i} data-testid={`overview-card-${i}`} className={`rounded-xl border p-4 ${toneClass}`}>
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider opacity-80">
                <Icon className="w-3.5 h-3.5" /> {c.label}
              </div>
              <div className="mt-2 text-2xl font-bold font-mono">{c.value}</div>
            </div>
          );
        })}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-display font-semibold">Quick add</h3>
        <p className="text-sm text-slate-600 mt-1">Capture money in/out as it happens: it only takes 10 seconds.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <QuickAddButton label="Rental income"    onClick={() => onAdd({record_type: "income"})} />
          <QuickAddButton label="Deposit received" onClick={() => onAdd({record_type: "deposit_received"})} />
          <QuickAddButton label="Repair"           onClick={() => onAdd({record_type: "repair_cost"})} />
          <QuickAddButton label="Fuel"             onClick={() => onAdd({record_type: "fuel_cost"})} />
          <QuickAddButton label="Cleaning"         onClick={() => onAdd({record_type: "cleaning_cost"})} />
          <QuickAddButton label="Other expense"    onClick={() => onAdd({record_type: "expense"})} />
        </div>
      </div>
      <p className="text-xs text-slate-500">Showing {ov.month}. Switching months & deeper reports live under the Reports tab.</p>
    </div>
  );
}

function QuickAddButton({ label, onClick }) {
  return (
    <button onClick={onClick} className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1">
      <Plus className="w-3 h-3" /> {label}
    </button>
  );
}


// ---------------------------------------------------------------------------
// Record list (shared by Income/Expenses/Deposits/Vehicle tabs)

function RecordList({ rows, title, onEdit, onDelete, startNew, showCustomer, showVendor }) {
  const total = rows.filter((r) => r.status === "paid")
                    .reduce((s, r) => s + Number(r.amount || 0), 0);

  const remove = async (r) => {
    if (!window.confirm("Delete this record? This cannot be undone.")) return;
    try {
      await api.delete(`/administration/records/${r.id}`);
      toast.success("Record deleted");
      onDelete();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`admin-records-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display font-semibold">{title}</h2>
          <p className="text-xs text-slate-500">{rows.length} record{rows.length === 1 ? "" : "s"} · Paid total {fmtPHP(total)}</p>
        </div>
        <button onClick={startNew} className="text-sm px-3 py-1.5 rounded-lg btn-brand inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          No records yet. Click <strong>Add</strong> to log your first one.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="px-5 py-3 flex items-start gap-3" data-testid={`record-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{r.title}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider ${statusTone(r.status)}`}>{r.status}</span>
                  <span className="text-xs text-slate-500">{recordMeta(r.record_type).label}</span>
                  {r._linked_booking && (
                    <Link to={`/app/bookings/${r._linked_booking.id}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${r._linked_booking.is_cancelled ? "border-rose-300 text-rose-700 bg-rose-50" : "border-slate-300 text-slate-700 bg-slate-50"}`}>
                      {r._linked_booking.is_cancelled ? "Booking cancelled" : (r._linked_booking.reference || "Booking")}
                    </Link>
                  )}
                  {r._linked_vehicle && (
                    <Link to={`/app/vehicles/${r._linked_vehicle.id}`}
                          className="text-[10px] px-2 py-0.5 rounded-full border border-slate-300 text-slate-700 bg-slate-50">
                      {r._linked_vehicle.name}
                    </Link>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {r.transaction_date}
                  {r.payment_method && <> · {r.payment_method}</>}
                  {showCustomer && r.customer_name && <> · {r.customer_name}</>}
                  {showVendor && r.vendor_name && <> · {r.vendor_name}</>}
                </div>
                {r.description && <p className="text-sm text-slate-600 mt-1">{r.description}</p>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-semibold">{fmtPHP(r.amount)}</div>
                <div className="mt-1 flex gap-1 justify-end">
                  <button onClick={() => onEdit({mode: "edit", data: {...r}})}
                          data-testid={`record-edit-${r.id}`}
                          className="text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1">
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(r)}
                          data-testid={`record-delete-${r.id}`}
                          className="text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Documents tab

function DocumentsPanel({ docs, onEdit, onReload, onAdd }) {
  const remove = async (d) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      await api.delete(`/administration/documents/${d.id}`);
      toast.success("Document deleted");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="admin-documents-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold">Business documents</h2>
          <p className="text-xs text-slate-500">Permits, registrations, insurance: store URLs &amp; notes for easy access.</p>
        </div>
        <button onClick={onAdd} data-testid="admin-add-doc-btn" className="text-sm px-3 py-1.5 rounded-lg btn-brand inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add document
        </button>
      </div>
      {docs.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          No documents stored yet. Add your first permit or registration to keep track of expiry dates.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => {
            const meta = DOC_TYPES.find((x) => x.code === d.document_type) || {label: d.document_type};
            const expiringSoon = d.expiry_date && d.expiry_date <= todayISO();
            return (
              <li key={d.id} className="px-5 py-3 flex items-start justify-between gap-3" data-testid={`doc-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{d.title}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">{meta.label}</span>
                    {expiringSoon && <span className="text-[10px] px-2 py-0.5 rounded-full border border-rose-300 bg-rose-50 text-rose-700 uppercase tracking-wider">Expired</span>}
                  </div>
                  {d.notes && <p className="text-sm text-slate-600 mt-1">{d.notes}</p>}
                  {d.expiry_date && <p className="text-xs text-slate-500 mt-0.5">Expires {d.expiry_date}</p>}
                  {d.file_url && (
                    <a href={d.file_url} target="_blank" rel="noreferrer"
                       className="text-xs text-[#1B4332] hover:underline">View linked file →</a>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => onEdit({mode: "edit", data: {...d}})}
                          className="text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">Edit</button>
                  <button onClick={() => remove(d)}
                          className="text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Reports tab

function ReportsPanel({ rp }) {
  if (!rp) return null;
  return (
    <div className="space-y-4" data-testid="admin-reports-panel">
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-semibold">By vehicle · {rp.month}</h2>
          <p className="text-xs text-slate-500">Paid income and expenses, grouped per vehicle.</p>
        </div>
        {rp.by_vehicle.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">No records linked to a vehicle yet this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <tr><th className="px-5 py-2">Vehicle</th><th className="px-3 py-2 text-right">Income</th><th className="px-3 py-2 text-right">Expense</th><th className="px-5 py-2 text-right">Net</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rp.by_vehicle.map((row) => (
                  <tr key={row.vehicle_id || "u"}>
                    <td className="px-5 py-2">
                      <div className="font-medium">{row.vehicle_name}</div>
                      {row.vehicle_plate && <div className="text-xs text-slate-500 font-mono">{row.vehicle_plate}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtPHP(row.income)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtPHP(row.expense)}</td>
                    <td className={`px-5 py-2 text-right font-mono font-semibold ${row.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtPHP(row.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-semibold">Pending / unpaid records</h2>
          <p className="text-xs text-slate-500">All records still marked as <strong>pending</strong>. Follow up before they slip through.</p>
        </div>
        {rp.pending_records.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Nothing pending. Good.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rp.pending_records.map((r) => (
              <li key={r.id} className="px-5 py-2 flex items-center justify-between text-sm">
                <span><span className="font-medium">{r.title}</span> · <span className="text-slate-500">{r.transaction_date}</span></span>
                <span className="font-mono">{fmtPHP(r.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Record editor modal

function RecordEditor({ editing, setEditing, vehicles, bookings, onClose, onSaved }) {
  const { mode, data } = editing;
  const update = (k, v) => setEditing({ ...editing, data: { ...data, [k]: v } });
  const [saving, setSaving] = useState(false);
  const { panelRef } = useAccessibleOverlay(true, onClose);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...data,
        amount: Number(data.amount || 0),
        linked_booking_id: data.linked_booking_id || null,
        linked_vehicle_id: data.linked_vehicle_id || null,
        description: data.description || null,
        customer_name: data.customer_name || null,
        vendor_name: data.vendor_name || null,
        category: data.category || null,
        receipt_url: data.receipt_url || null,
      };
      if (mode === "new") {
        await api.post("/administration/records", body);
        toast.success("Record added");
      } else {
        await api.put(`/administration/records/${data.id}`, body);
        toast.success("Record updated");
      }
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid="record-editor">
      <div className="min-h-full flex items-center justify-center p-4">
        <form ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="record-editor-title"
              onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 max-w-xl w-full my-4">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 id="record-editor-title" className="font-display font-semibold">{mode === "new" ? "Add record" : "Edit record"}</h3>
            <button type="button" onClick={onClose} aria-label="Close record editor" data-testid="record-editor-close" className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 grid sm:grid-cols-2 gap-4">
            <F label="Type" className="sm:col-span-2">
              <select required value={data.record_type} onChange={(e) => update("record_type", e.target.value)} data-testid="record-type-select"
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                {RECORD_TYPES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
            </F>
            <F label="Title" className="sm:col-span-2">
              <input required value={data.title} onChange={(e) => update("title", e.target.value)}
                     placeholder="e.g. Booking BK-… balance, Bumper repair, Fuel top-up"
                     data-testid="record-title-input"
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="Amount (PHP)">
              <input required type="number" min="0" step="0.01" value={data.amount}
                     onChange={(e) => update("amount", e.target.value)} data-testid="record-amount-input"
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none font-mono" />
            </F>
            <F label="Date">
              <input type="date" value={data.transaction_date || ""} onChange={(e) => update("transaction_date", e.target.value)}
                     data-testid="record-date-input"
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="Payment method">
              <select value={data.payment_method || ""} onChange={(e) => update("payment_method", e.target.value)} data-testid="record-method-select"
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </F>
            <F label="Status">
              <select value={data.status} onChange={(e) => update("status", e.target.value)} data-testid="record-status-select"
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </F>
            <F label="Link to booking (optional)">
              <select value={data.linked_booking_id || ""} onChange={(e) => update("linked_booking_id", e.target.value || null)} data-testid="record-booking-select"
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                <option value="">- none -</option>
                {bookings.slice(0, 200).map((b) => {
                  const label = `${b.booking_reference} · ${b.customer_name}${b.status === "cancelled" ? " (cancelled)" : ""}`;
                  return <option key={b.id} value={b.id}>{label}</option>;
                })}
              </select>
            </F>
            <F label="Link to vehicle (optional)">
              <select value={data.linked_vehicle_id || ""} onChange={(e) => update("linked_vehicle_id", e.target.value || null)} data-testid="record-vehicle-select"
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                <option value="">- none -</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} {v.plate_number ? `· ${v.plate_number}` : ""}</option>)}
              </select>
            </F>
            <F label="Customer (optional)">
              <input value={data.customer_name || ""} onChange={(e) => update("customer_name", e.target.value)}
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="Vendor / supplier (optional)">
              <input value={data.vendor_name || ""} onChange={(e) => update("vendor_name", e.target.value)}
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="Notes (optional)" className="sm:col-span-2">
              <textarea rows={3} value={data.description || ""} onChange={(e) => update("description", e.target.value)} data-testid="record-notes-input"
                        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
          </div>
          <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="record-editor-save"
                    className="px-4 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60">
              {saving ? "Saving…" : (mode === "new" ? "Add record" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Document editor modal

function DocumentEditor({ editing, setEditing, vehicles, onClose, onSaved }) {
  const { mode, data } = editing;
  const update = (k, v) => setEditing({ ...editing, data: { ...data, [k]: v } });
  const [saving, setSaving] = useState(false);
  const { panelRef } = useAccessibleOverlay(true, onClose);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...data,
        notes: data.notes || null,
        file_url: data.file_url || null,
        expiry_date: data.expiry_date || null,
        linked_vehicle_id: data.linked_vehicle_id || null,
      };
      if (mode === "new") {
        await api.post("/administration/documents", body);
        toast.success("Document added");
      } else {
        await api.put(`/administration/documents/${data.id}`, body);
        toast.success("Document updated");
      }
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid="doc-editor">
      <div className="min-h-full flex items-center justify-center p-4">
        <form ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="document-editor-title"
              onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full my-4">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 id="document-editor-title" className="font-display font-semibold">{mode === "new" ? "Add document" : "Edit document"}</h3>
            <button type="button" onClick={onClose} aria-label="Close document editor" className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 grid gap-4">
            <F label="Document type">
              <select required value={data.document_type} onChange={(e) => update("document_type", e.target.value)} data-testid="doc-type-select"
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                {DOC_TYPES.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
              </select>
            </F>
            <F label="Title">
              <input required value={data.title} onChange={(e) => update("title", e.target.value)} data-testid="doc-title-input"
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="Notes (optional)">
              <textarea rows={3} value={data.notes || ""} onChange={(e) => update("notes", e.target.value)}
                        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="File URL (optional)">
              <input value={data.file_url || ""} onChange={(e) => update("file_url", e.target.value)}
                     placeholder="https://drive.google.com/…"
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="Expiry date (optional)">
              <input type="date" value={data.expiry_date || ""} onChange={(e) => update("expiry_date", e.target.value)}
                     className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </F>
            <F label="Link to vehicle (optional)">
              <select value={data.linked_vehicle_id || ""} onChange={(e) => update("linked_vehicle_id", e.target.value || null)}
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                <option value="">- none -</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} {v.plate_number ? `· ${v.plate_number}` : ""}</option>)}
              </select>
            </F>
          </div>
          <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="doc-editor-save"
                    className="px-4 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60">
              {saving ? "Saving…" : (mode === "new" ? "Add document" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function F({ label, children, className = "" }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}
